import { useEffect, useMemo, useRef, useState } from 'react';
import MonacoEditor from 'react-monaco-editor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faRotateLeft, faRotateRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { EditorStatusBar } from './EditorStatusBar';
import 'monaco-editor/min/vs/editor/editor.main.css';
import 'monaco-editor/esm/vs/base/browser/ui/codicons/codicon/codicon.css';

export interface MultiTabMonacoEditorProps {
	tabs: Array<{ id?: string; name: string; language: string; code: string; active?: boolean }>;
	width?: string;
	height?: string;
	cfg?: any;
	options?: any;
	codeChanged: (content: string, tab: any, idx: number) => void;
	activeIndex?: number; // controlled active tab index
	onActiveIndexChange?: (idx: number) => void; // notify parent on change
	showHeaderTabs?: boolean; // render internal header tabs (default true)
	rightText?: string;
	onModifiedChange?: (m: boolean) => void;
	currentTheme?: string;
	closableTabs?: boolean; // show close button on tabs
	onTabClose?: (index: number, tab: any) => void; // called when tab close button is clicked
	rightSidebarCollapsed?: boolean;
	onRightSidebarToggle?: () => void;

	// Allow extension of optional UI props without breaking JSX type-checking.
	[key: string]: any;
}

export const MultiTabMonacoEditor = (props: MultiTabMonacoEditorProps) => {
	const editorRef = useRef<any>(null);
	const monacoRef = useRef<any>(null);
	const modelsRef = useRef<Map<string, any>>(new Map());
	const viewStatesRef = useRef<Map<string, any>>(new Map());
	const [status, setStatus] = useState<{ lines: number; lineNumber: number; column: number }>({ lines: 0, lineNumber: 1, column: 1 });

	const initialIndex = useMemo(() => {
		const idx = props.tabs.findIndex(t => t.active === true);
		return idx >= 0 ? idx : 0;
	}, [props.tabs]);
	const [internalActiveIndex, setInternalActiveIndex] = useState<number>(initialIndex);
	const isControlled = typeof props.activeIndex === 'number';
	const activeIndex = isControlled ? (props.activeIndex as number) : internalActiveIndex;

	// Active tab data-URL preview (V2 filesystem uploads store media as data URLs in `tab.code`)
	const activeTab = props.tabs[activeIndex];
	const activeCode = activeTab?.code || '';
	const isDataUrl = activeCode.startsWith('data:');
	const isImageDataUrl = isDataUrl && activeCode.startsWith('data:image/');
	const showPreviewUnavailable = isDataUrl && !isImageDataUrl;

	const getMode = (mode: string): string => {
		if (!mode) return 'plaintext';
		const m = mode.toLowerCase();
		if (m === 'c' || m === 'cpp' || m === 'c++') return 'cpp';
		if (m === 'bash' || m === 'sh' || m === 'shell') return 'shell';
		if (m === 'js' || m === 'javascript') return 'javascript';
		if (m === 'ts' || m === 'typescript') return 'typescript';
		if (m === 'html' || m === 'html5' || m === 'web') return 'html';
		if (m === 'css' || m === 'css3') return 'css';
		if (m === 'py' || m === 'python') return 'python';
		if (m === 'text' || m === 'plaintext') return 'plaintext';
		return m;
	};

	const combinedOptions = { ...(props.cfg?.options || {}), ...(props.options || {}) };
	if (combinedOptions.automaticLayout === undefined) combinedOptions.automaticLayout = true;
	if (combinedOptions.scrollBeyondLastLine === undefined) combinedOptions.scrollBeyondLastLine = false;
	if (combinedOptions.contextmenu === undefined) combinedOptions.contextmenu = true;
	const isDark = props.currentTheme === 'dark';
	// Use dark/light theme based on isDark, unless combinedOptions.theme is explicitly set to something other than the default
	const defaultTheme = isDark ? 'learningThemeDark' : 'learningTheme';
	const desiredTheme = (combinedOptions && combinedOptions.theme && combinedOptions.theme !== 'learningTheme' && combinedOptions.theme !== 'learningThemeDark') 
		? combinedOptions.theme 
		: defaultTheme;

	const editorWillMount = (monaco: any) => {
		monacoRef.current = monaco;
		
		// Remove mismatched language markers (language-based filtering)
		try {
			monaco.editor.onDidChangeMarkers((uris: any[]) => {
				uris.forEach((uri: any) => {
					const model = monaco.editor.getModel(uri);
					if (model) {
						const lang = model.getLanguageId();
						
						// Remove TypeScript/JS markers for non-JS/TS files
						if (lang !== 'javascript' && lang !== 'typescript' && 
						    lang !== 'javascriptreact' && lang !== 'typescriptreact') {
							monaco.editor.setModelMarkers(model, 'typescript', []);
						}
						
						// Remove CSS markers for non-CSS files
						if (lang !== 'css' && lang !== 'scss' && lang !== 'less') {
							monaco.editor.setModelMarkers(model, 'css', []);
						}
						
						// Remove HTML markers for non-HTML files
						if (lang !== 'html') {
							monaco.editor.setModelMarkers(model, 'html', []);
						}
					}
				});
			});
		} catch (e) {
			console.warn('[MultiTab] Could not setup marker filter:', e);
		}
		
		try {
			// Light theme
			monaco.editor.defineTheme('learningTheme', {
				base: 'vs',
				inherit: true,
				rules: [
					{ token: 'comment', foreground: '6a9955', fontStyle: 'italic' },
					{ token: 'keyword', foreground: '0000ff', fontStyle: 'bold' },
					{ token: 'string', foreground: 'a31515' },
					{ token: 'number', foreground: '098658' },
					{ token: 'function', foreground: '795e26' }
				],
				colors: {
					'editor.foreground': '#2d3748',
					'editor.background': '#ffffff',
					'editor.selectionBackground': '#007acc40',
					'editor.lineHighlightBackground': '#f8f9fa',
					'editorLineNumber.foreground': '#a0aec0',
					'editorCursor.foreground': '#007acc'
				}
			});
			// Dark theme
			monaco.editor.defineTheme('learningThemeDark', {
				base: 'vs-dark',
				inherit: true,
				rules: [
					{ token: 'comment', foreground: '6a9955', fontStyle: 'italic' },
					{ token: 'keyword', foreground: '569cd6', fontStyle: 'bold' },
					{ token: 'string', foreground: 'ce9178' },
					{ token: 'number', foreground: 'b5cea8' },
					{ token: 'function', foreground: 'dcdcaa' }
				],
				colors: {
					'editor.foreground': '#e5e7eb',
					'editor.background': '#1e1e1e',
					'editor.selectionBackground': '#264f78',
					'editor.lineHighlightBackground': '#2a2d2e',
					'editorLineNumber.foreground': '#858585',
					'editorCursor.foreground': '#aeafad'
				}
			});
		} catch {}
	};

	// Track which models were created by us (to avoid triggering codeChanged on setValue)
	const updatingModelRef = useRef<Set<string>>(new Set());

	const ensureModel = (tab: any, index: number, forceContent?: string) => {
		const monaco = monacoRef.current;
		if (!monaco) return null;
		
		// Use tab.path (V2) or original index as unique key
		const key = tab.path || (tab.index !== undefined ? `idx-${tab.index}` : `idx-${index}`);
		let model = modelsRef.current.get(key);
		
		const desiredCode = forceContent !== undefined ? forceContent : (tab.code || '');
		const desiredLanguage = getMode(tab.language);
		
		if (model) {
			// Check if language changed - if so, recreate the model
			const currentLanguage = model.getLanguageId();
			if (currentLanguage !== desiredLanguage) {
				console.log(`[MultiTab] Language mismatch for ${key}: ${currentLanguage} -> ${desiredLanguage}, recreating model`);
				// Dispose old model and recreate
				model.dispose();
				modelsRef.current.delete(key);
				model = null;
			}
		}
		
		if (!model) {
			// Create new model with initial code
			const uri = monaco.Uri.parse(`inmemory://tabs/${key}`);
			
			// Dispose any orphaned model with same URI
			const existingModel = monaco.editor.getModel(uri);
			if (existingModel) {
				existingModel.dispose();
			}
			
			model = monaco.editor.createModel(desiredCode, desiredLanguage, uri);
			
			// Important: Capture tab reference at model creation time
			const capturedTab = { ...tab };
			const capturedKey = key;
			model.onDidChangeContent(() => {
				// Skip if we're programmatically updating the model
				if (updatingModelRef.current.has(capturedKey)) return;
				
				const newValue = model.getValue();
				const originalIndex = capturedTab.index !== undefined ? capturedTab.index : index;
				props.codeChanged(newValue, capturedTab, originalIndex);
			});
			modelsRef.current.set(key, model);
		}
		return model;
	};

	// Get stable key for a tab (path for V2, or original index)
	const getTabKey = (tab: any, index: number) => {
		return tab.path || (tab.index !== undefined ? `idx-${tab.index}` : `idx-${index}`);
	};

	// Sync Monaco models when tab.code updates from outside (e.g. agent writes to VFS)
	// Without this, Monaco updates typically only after a tab switch/mount.
	useEffect(() => {
		if (!monacoRef.current) return;

		for (let i = 0; i < props.tabs.length; i++) {
			const tab = props.tabs[i];
			const key = getTabKey(tab, i);
			const model = modelsRef.current.get(key);
			if (!model) continue;

			const expectedValue = tab.code || "";
			const currentValue = model.getValue();
			if (currentValue !== expectedValue) {
				// Avoid triggering codeChanged when we set the value programmatically
				updatingModelRef.current.add(key);
				model.setValue(expectedValue);
				updatingModelRef.current.delete(key);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.tabs]);

	const openTab = (index: number) => {
		const editor = editorRef.current;
		const monaco = monacoRef.current;
		const tab = props.tabs[index];
		if (!tab) return;

		// When Monaco is mounted, keep view/model switching behavior.
		// When Monaco is NOT mounted (data URL preview mode), we still must update active tab.
		if (editor) {
			// save current view state using stable key
			try {
				const currentModel = editor.getModel && editor.getModel();
				if (currentModel && props.tabs[activeIndex]) {
					const currentKey = getTabKey(props.tabs[activeIndex], activeIndex);
					viewStatesRef.current.set(currentKey, editor.saveViewState());
				}
			} catch {}
		}

		if (editor && monaco) {
			// switch to new tab
			const key = getTabKey(tab, index);
			const model = ensureModel(tab, index);
			if (model) {
				// Sync model content with tab.code (in case external state changed)
				const currentModelValue = model.getValue();
				const expectedValue = tab.code || '';
				if (currentModelValue !== expectedValue) {
					// Mark that we're updating programmatically (don't trigger codeChanged)
					updatingModelRef.current.add(key);
					model.setValue(expectedValue);
					updatingModelRef.current.delete(key);
				}

				// Verify language is correct (ensureModel should handle this, but double-check)
				const desiredLanguage = getMode(tab.language);
				const currentLanguage = model.getLanguageId();
				if (currentLanguage !== desiredLanguage) {
					console.warn(`[MultiTab] Language still mismatched after ensureModel: ${currentLanguage} vs ${desiredLanguage}`);
					monaco.editor.setModelLanguage(model, desiredLanguage);
				}

				editor.setModel(model);
				const vs = viewStatesRef.current.get(key);
				if (vs) editor.restoreViewState(vs);
				editor.focus();
			}
		}

		if (!isControlled) {
			setInternalActiveIndex(index);
		}
		if (props.onActiveIndexChange) props.onActiveIndexChange(index);
	};

	const editorDidMount = (editor: any, monaco: any) => {
		editorRef.current = editor;
		monacoRef.current = monaco;
		try { (window as any).__monaco_editor__ = editor; } catch {}
		try { monaco.editor.setTheme(desiredTheme); } catch {}
		
		// initialize models for all tabs
		props.tabs.forEach((t, i) => ensureModel(t, i));
		
		// context menu enforcement
		editor.updateOptions({ contextmenu: combinedOptions.contextmenu !== false });
		// notify modified=false baseline
		try { props.onModifiedChange && props.onModifiedChange(false); } catch {}
		
		// Set the correct model after a micro-task to override react-monaco-editor's internal model
		setTimeout(() => {
			const tab = props.tabs[activeIndex];
			if (tab && editorRef.current) {
				const key = getTabKey(tab, activeIndex);
				const model = modelsRef.current.get(key);
				if (model) {
					editorRef.current.setModel(model);
					editorRef.current.focus();
				}
			}
		}, 10);
	};

	useEffect(() => {
		return () => {
			// dispose models on unmount
			modelsRef.current.forEach(m => { try { m.dispose(); } catch {} });
			modelsRef.current.clear();
		};
	}, []);

	// Respond to controlled activeIndex changes or when new tabs are added
	useEffect(() => {
		if (editorRef.current && monacoRef.current && isControlled) {
			openTab(activeIndex);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isControlled, activeIndex, props.tabs.length]);

	// Update Monaco theme when currentTheme prop changes
	useEffect(() => {
		const monaco = monacoRef.current;
		if (monaco) {
			try {
				const isDarkNow = props.currentTheme === 'dark';
				const newTheme = isDarkNow ? 'learningThemeDark' : 'learningTheme';
				monaco.editor.setTheme(newTheme);
			} catch (e) {
				console.error('[MultiTabMonacoEditor] Failed to update Monaco theme:', e);
			}
		}
	}, [props.currentTheme]);

	return (
		<div className="code-section" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
			{props.showHeaderTabs !== false && (
				<div className="code-header">
					<div className="panel-tabs">
						{props.tabs.map((t, i) => (
							<button
								key={i}
								className={`panel-tab${i === activeIndex ? ' active' : ''}`}
								onClick={() => openTab(i)}
								style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
							>
								{t.name}
								{props.closableTabs && props.onTabClose && (
									<span
										className="tab-close-btn"
										onClick={(e) => {
											e.stopPropagation();
											props.onTabClose!(i, t);
										}}
										style={{
											marginLeft: '4px',
											opacity: 0.6,
											fontSize: '10px',
											cursor: 'pointer',
											padding: '2px',
											borderRadius: '2px',
										}}
										onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
										onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
									>
										<FontAwesomeIcon icon={faTimes} />
									</span>
								)}
							</button>
						))}
					</div>
					<div className="editor-actions" style={{marginLeft:'auto', display:'flex', gap: '8px'}}>
						<button className="panel-tab" onClick={() => { try { editorRef.current?.trigger('keyboard', 'undo', null); } catch {} }}>
							<FontAwesomeIcon icon={faRotateLeft} />
						</button>
						<button className="panel-tab" onClick={() => { try { editorRef.current?.trigger('keyboard', 'redo', null); } catch {} }}>
							<FontAwesomeIcon icon={faRotateRight} />
						</button>
					</div>
				</div>
			)}
			<div style={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
				{/* Render media previews for data URLs; otherwise fall back to Monaco. */}
				{isImageDataUrl ? (
					<div
						style={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							overflow: 'auto',
							padding: 12,
						}}
					>
						<img
							src={activeCode}
							alt={activeTab?.name || 'preview'}
							style={{
								maxWidth: '100%',
								maxHeight: '100%',
								objectFit: 'contain',
								borderRadius: 4,
								boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
							}}
						/>
					</div>
				) : showPreviewUnavailable ? (
					<div
						style={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							overflow: 'auto',
							padding: 12,
							color: props.currentTheme === 'dark' ? '#e5e7eb' : '#2d3748',
							background: props.currentTheme === 'dark' ? 'rgba(30,30,30,0.6)' : 'rgba(255,255,255,0.7)',
							backdropFilter: 'blur(2px)',
						}}
					>
						<span>Vorschau nicht verfügbar</span>
					</div>
				) : (
					<MonacoEditor
						width={props.width || '100%'}
						height={props.height || '100%'}
						language={getMode(props.tabs[activeIndex]?.language)}
						theme={desiredTheme}
						options={combinedOptions}
						editorWillMount={editorWillMount}
						editorDidMount={editorDidMount}
					/>
				)}
			</div>
			<EditorStatusBar status={status} rightText={props.rightText || 'Gespeichert'} />
		</div>
	);
};

export default MultiTabMonacoEditor;


